import { useEffect, useRef, useState } from 'react';
import { GAME_RULES } from '@continuum/show-core';
import type { ThreadTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';
import { EraBackdrop, ShipSprite } from './art.js';

const THEME: Record<ThreadTask['theme'], { wall: string; glow: string; bg: string }> = {
  stars: { wall: '#6fb7ff', glow: 'rgba(111,183,255,.5)', bg: '#060b18' },
  neon: { wall: '#ff4fd8', glow: 'rgba(255,79,216,.55)', bg: '#0d0618' },
  debris: { wall: '#ff2e3f', glow: 'rgba(255,46,63,.5)', bg: '#120305' },
  dust: { wall: '#e08a3c', glow: 'rgba(224,138,60,.5)', bg: '#140d05' },
  canopy: { wall: '#5c7d4e', glow: 'rgba(92,125,78,.5)', bg: '#1a2016' },
};

interface Gate {
  x: number;
  centre: number;
  passed: boolean;
}

/**
 * NAVIGATOR — one touch lifts, gravity pulls. Thread the gaps.
 *
 * The most-copied one-button game there is, which is the point: nobody needs telling
 * how it works, and it reads instantly across a dinner table. Gentle since the test evening
 * (2026-09-28, "way too fast"): the ship hangs still until the first tap, floats rather than
 * drops, the gates come slowly and never jump far from the last, the top and bottom only stop
 * the ship, and a clipped gate is a mistake but still counts as flown.
 */
export function Thread({ task, era, onProgress, onWin, onMistake, onFrame, paused = false, s }: GameProps<ThreadTask>) {
  const { tickMs, lift, gravity, maxFall, shipY, gateEvery, gateStartX, shipX, drift, restMs } = GAME_RULES.thread;
  const theme = THEME[task.theme];
  const [y, setY] = useState<number>(shipY);
  const [gates, setGates] = useState<Gate[]>([]);
  const [cleared, setCleared] = useState(0);
  const [dead, setDead] = useState(false);
  const vel = useRef(0);
  const yRef = useRef<number>(shipY);
  const gatesRef = useRef<Gate[]>([]);
  const won = useRef(false);
  /** Nothing moves until the first tap: the guest starts flying when they are ready. */
  const started = useRef(false);
  /** When the guest last tapped: with no tap for `restMs`, the gates wait for them. */
  const lastTap = useRef(0);
  const [resting, setResting] = useState(false);
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };
  const hold = useRef(paused);
  hold.current = paused;

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let spawn = 0;
    let centre = 0.5;

    const step = (now: number) => {
      // Covered by an alarm or a sheet, or not flying yet: the ship hangs where it is.
      if (hold.current || !started.current) {
        last = now;
        raf = requestAnimationFrame(step);
        return;
      }
      const dt = Math.min(48, now - last) / tickMs;
      last = now;

      // It sinks, but never faster than a steady drift.
      vel.current = Math.min(maxFall, vel.current + gravity * dt);
      let nextY = yRef.current + (vel.current * dt) / 100;
      // The top and the bottom only stop the ship: losing is never the point.
      if (nextY < 0.04 || nextY > 0.96) {
        vel.current = 0;
        nextY = Math.min(0.96, Math.max(0.04, nextY));
      }
      yRef.current = nextY;
      setY(nextY);

      // Hands off the phone: the ship settles, and the gates hold still until the next tap.
      const idle = performance.now() - lastTap.current > restMs;
      setResting(idle);
      if (idle) {
        raf = requestAnimationFrame(step);
        return;
      }

      spawn -= dt;
      let next = gatesRef.current.map((g) => ({ ...g, x: g.x - (task.speed * dt) / 100 }));
      if (spawn <= 0) {
        spawn = gateEvery;
        // Each opening a short climb or drop from the last, never across the whole board.
        centre = Math.min(0.72, Math.max(0.28, centre + (Math.random() - 0.5) * 2 * drift));
        next.push({ x: gateStartX, centre, passed: false });
      }
      next = next.filter((g) => g.x > -0.15);
      for (const g of next) {
        if (!g.passed && g.x < shipX) {
          g.passed = true;
          // Every gate flown counts; clipping one is a mistake, and nothing more.
          if (Math.abs(yRef.current - g.centre) > task.gap / 2) cbs.current.onMistake?.();
          setCleared((c) => {
            const total = c + 1;
            cbs.current.onProgress(Math.round((total / task.target) * 100));
            if (total >= task.target && !won.current) {
              won.current = true;
              // Not inside the state update: WELL DONE is another component's state.
              queueMicrotask(() => cbs.current.onWin());
            }
            return total;
          });
        }
      }
      gatesRef.current = next;
      setGates(next);

      // A glance at this screen, for whoever is watching.
      cbs.current.onFrame(
        `y:${Math.round(yRef.current * 100)}|` +
          gatesRef.current
            .slice(0, 3)
            .map((g, i) => `g${i}x:${Math.round(g.x * 100)},g${i}c:${Math.round(g.centre * 100)}`)
            .join('|'),
      );

      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [tickMs, gravity, maxFall, task.speed, task.target, task.gap, gateEvery, gateStartX, shipX, drift, restMs]);

  // Brushing a wall costs nothing but a flash — losing is never the point.
  useEffect(() => {
    for (const g of gates) {
      if (g.x < 0.26 && g.x > 0.1) {
        const half = task.gap / 2;
        if (y < g.centre - half || y > g.centre + half) {
          setDead(true);
          setTimeout(() => setDead(false), 200);
        }
      }
    }
  }, [gates, y, task.gap]);

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ marginBottom: 6, fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: theme.wall }}>
        {s.threadCount(cleared, task.target)}
      </div>
      <button
        data-nopress
        onPointerDown={() => {
          if (hold.current) return;
          started.current = true;
          lastTap.current = performance.now();
          vel.current = lift;
        }}
        style={{
          position: 'relative',
          width: '100%',
          height: 260,
          borderRadius: 14,
          border: `1px solid ${theme.wall}55`,
          background: theme.bg,
          overflow: 'hidden',
          padding: 0,
          touchAction: 'none',
          cursor: 'pointer',
        }}
      >
        <EraBackdrop era={era} />

        {gates.map((g, i) => {
          const half = task.gap / 2;
          const threaded = g.passed && Math.abs(yRef.current - g.centre) <= half;
          return (
            <div
              key={i}
              style={threaded ? { opacity: 0.35, transition: 'opacity .3s' } : undefined}
            >
              <div
                style={{
                  position: 'absolute',
                  left: `${g.x * 100}%`,
                  top: 0,
                  width: 26,
                  height: `${(g.centre - half) * 100}%`,
                  background: `linear-gradient(180deg,${theme.wall}22,${theme.wall})`,
                  boxShadow: `0 0 18px ${theme.glow}`,
                  borderRadius: '0 0 4px 4px',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${g.x * 100}%`,
                  top: `${(g.centre + half) * 100}%`,
                  width: 26,
                  bottom: 0,
                  background: `linear-gradient(0deg,${theme.wall}22,${theme.wall})`,
                  boxShadow: `0 0 18px ${theme.glow}`,
                  borderRadius: '4px 4px 0 0',
                }}
              />
            </div>
          );
        })}

        <div
          style={{
            position: 'absolute',
            left: '18%',
            top: `${y * 100}%`,
            width: 30,
            height: 30,
            marginTop: -15,
            marginLeft: -6,
            filter: dead ? 'none' : `drop-shadow(0 0 6px ${theme.glow})`,
          }}
        >
          <ShipSprite era={era} tilt={Math.max(-28, Math.min(38, vel.current * 14))} dead={dead} />
        </div>

      </button>
      <div
        style={{
          marginTop: 8,
          textAlign: 'center',
          fontFamily: mono,
          fontSize: 12,
          letterSpacing: 1,
          // Lit while the board waits for a tap, before the first one or after a rest.
          color: resting || !started.current ? theme.wall : '#9db3c2',
        }}
      >
        {s.threadTap}
      </div>
    </div>
  );
}
